import { ColumnDef } from '@tanstack/react-table';
import { t } from 'i18next';
import { EllipsisVerticalIcon, PencilIcon, Trash2Icon } from 'lucide-react';

import { RowActionIndicator } from '@/components/atoms/RowActionIndicator/RowActionIndicator';
import TimestampTooltip from '@/components/atoms/TimestampTooltip/TimestampTooltip';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { SortableHeader } from '@/components/ui/table/sortable-header';
import { COLUMN_PRESETS } from '@/util/constants';
import { timeFromNow } from '@/util/dateUtils';

import { SPEECH_PROVIDER_ICONS, SPEECH_PROVIDER_NAMES } from './helper';
import { SpeechModel } from '../../types';

interface ColumnsProps {
  onEdit: (speechModel: SpeechModel) => void;
  onDelete: (speechModel: SpeechModel) => void;
  canWrite: boolean;
  canDelete: boolean;
}

export const createColumns = ({
  onEdit,
  onDelete,
  canWrite,
  canDelete,
}: ColumnsProps): ColumnDef<SpeechModel>[] => [
  {
    id: 'name',
    accessorKey: 'name',
    enableHiding: false,
    ...COLUMN_PRESETS.FLEX,
    meta: { preset: 'FLEX', className: 'pl-4' },
    header: ({ column }) => <SortableHeader column={column}>{t('common.name')}</SortableHeader>,
    cell: ({ row }) => (
      <div className="flex items-center gap-3">
        <span className="min-w-0 truncate font-medium">{row.original.name}</span>
        <RowActionIndicator action="edit" />
      </div>
    ),
  },
  {
    id: 'provider',
    accessorFn: (row) => SPEECH_PROVIDER_NAMES[row.provider] ?? row.provider,
    ...COLUMN_PRESETS.LG,
    meta: { preset: 'LG' },
    header: ({ column }) => (
      <SortableHeader column={column}>
        {t('developer_tools.language_models_provider')}
      </SortableHeader>
    ),
    cell: ({ row }) => (
      <div className="flex items-center gap-2">
        {SPEECH_PROVIDER_ICONS[row.original.provider]}
        <span className="truncate">
          {SPEECH_PROVIDER_NAMES[row.original.provider] ?? row.original.provider}
        </span>
      </div>
    ),
  },
  {
    id: 'api_key',
    accessorFn: (row) => row.config.api_key,
    enableSorting: false,
    ...COLUMN_PRESETS.LG,
    meta: { preset: 'LG', label: 'developer_tools.sidebar_api_key' },
    header: () => t('developer_tools.sidebar_api_key'),
    cell: ({ row }) => (
      <span className="truncate font-mono text-foreground-muted">
        {row.original.config.api_key}
      </span>
    ),
  },
  {
    id: 'created',
    accessorKey: 'created',
    ...COLUMN_PRESETS.MD,
    meta: { preset: 'MD', label: 'common.created' },
    header: ({ column }) => <SortableHeader column={column}>{t('common.created')}</SortableHeader>,
    cell: ({ row }) => {
      const { created } = row.original;
      return created ? (
        <TimestampTooltip timestamp={created}>{timeFromNow(created)}</TimestampTooltip>
      ) : (
        '-'
      );
    },
  },
  {
    id: 'actions',
    enableHiding: false,
    enableSorting: false,
    ...COLUMN_PRESETS.SM,
    meta: { preset: 'SM' },
    cell: ({ row }) => (
      <DropdownMenu modal={false}>
        <DropdownMenuTrigger asChild>
          <Button
            variant="ghost-neutral"
            size="icon-sm"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
            }}
          >
            <EllipsisVerticalIcon size={16} />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" onClick={(e) => e.stopPropagation()}>
          <DropdownMenuItem
            onSelect={() => onEdit(row.original)}
            className="flex items-center gap-2"
            disabled={!canWrite}
          >
            <PencilIcon size={16} className="text-foreground-muted" />
            {t('common.edit')}
          </DropdownMenuItem>
          <DropdownMenuItem
            onSelect={() => onDelete(row.original)}
            variant="destructive"
            className="flex items-center gap-2"
            disabled={!canDelete}
          >
            <Trash2Icon size={16} />
            {`${t('common.delete')}…`}
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    ),
  },
];
